import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, 
  LogOut, 
  Heart,
  Home,
  ChevronRight,
  Search,
  Download,
  Eye,
  X,
  Phone,
  Clock,
  AlertCircle,
  User,
  Calendar,
  MapPin,
  FileText,
  UserCheck,
  UserX,
  Settings,
  TrendingUp,
  Users,
  Activity,
  Globe
} from 'lucide-react';

const AdminNewsletter = () => {
  const { user, logout, isAuthenticated, isReadOnly } = useAuth();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [loadingActions, setLoadingActions] = useState({});
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    thisMonth: 0
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSubscriptions();
    fetchStats();
  }, [isAuthenticated, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await fetch(`http://localhost:8000/api/newsletter?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.data || []);
      } else {
        console.error('Erreur lors du chargement des abonnements');
        showNotification('Erreur lors du chargement des abonnements', 'error');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('http://localhost:8000/api/newsletter/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || stats);
      }
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  };

  const toggleSubscriptionStatus = async (subscriptionId, currentStatus) => {
    try {
      setLoadingActions(prev => ({ ...prev, [subscriptionId]: true }));
      
      const newStatus = currentStatus ? 'inactive' : 'active';
      const response = await fetch(`http://localhost:8000/api/newsletter/${subscriptionId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
// Activity
      if (response.ok) {
        showNotification(
          `Abonnement ${newStatus === 'active' ? 'activé' : 'désactivé'} avec succès`, 
          'success'
        );
        fetchSubscriptions();
        fetchStats();
      } else {
        showNotification('Erreur lors de la mise à jour', 'error');
      }
    } catch (error) {
      console.error('Update error:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoadingActions(prev => {
        const newState = { ...prev };
        delete newState[subscriptionId];
        return newState;
      });
    }
  };

  const deleteSubscription = async (subscriptionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet abonnement ?')) {
      return;
    }

    try {
      const response = await fetch(`http://localhost:8000/api/newsletter/${subscriptionId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showNotification('Abonnement supprimé avec succès', 'success');
        fetchSubscriptions();
        fetchStats();
      } else {
        showNotification('Erreur lors de la suppression', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('Erreur de connexion', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`http://localhost:8000/api/newsletter/export?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Créer un fichier CSV
        const headers = Object.keys(data.data[0] || {});
        const csvContent = [
          headers.join(','),
          ...data.data.map(row => headers.map(header => 
            `"${String(row[header] || '').replace(/"/g, '""')}"`
          ).join(','))
        ].join('\n');
        
        // Télécharger le fichier
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `newsletter_subscriptions_${new Date().toISOString().split('T')[0]}.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
        showNotification('Export réalisé avec succès', 'success');
      } else {
        showNotification('Erreur lors de l\'export', 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Erreur lors de l\'export', 'error');
    }
  };

  const openSubscriptionModal = (subscription) => {
    setSelectedSubscription(subscription);
    setShowSubscriptionModal(true);
  };

  const closeSubscriptionModal = () => {
    setSelectedSubscription(null);
    setShowSubscriptionModal(false);
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
        isActive 
          ? 'bg-green-100 text-green-800 border-green-200'
          : 'bg-red-100 text-red-800 border-red-200'
      }`}>
        {isActive ? 'Actif' : 'Inactif'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPreferences = (preferences) => {
    if (!preferences) return 'Non définies';
    
    const prefs = [];
    if (preferences.events) prefs.push('Événements');
    if (preferences.news) prefs.push('Actualités');
    if (preferences.announcements) prefs.push('Annonces');
    
    return prefs.length > 0 ? prefs.join(', ') : 'Aucune';
  };

  const getFrequencyLabel = (preferences) => {
    if (!preferences || !preferences.frequency) return 'Toutes';
    
    const labels = {
      'all': 'Toutes',
      'weekly': 'Hebdomadaire',
      'monthly': 'Mensuelle'
    };
    
    return labels[preferences.frequency] || preferences.frequency;
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [searchTerm, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-500 to-purple-600">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
            {/* Navigation breadcrumb */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1 sm:space-x-2 text-gray-600">
                <Home className="w-4 h-4" />
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="font-medium text-gray-900 text-sm sm:text-base">Dashboard</span>
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-purple-600 font-semibold text-sm sm:text-base">Newsletter</span>
              </div>
            </div>

            {/* User info et actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-6">
              <div className="text-left sm:text-right order-2 sm:order-1">
                <p className="text-sm font-medium text-gray-900">
                  {user?.first_name || user?.name || 'Admin'} {user?.last_name || ''}
                </p>
                <p className="text-xs text-gray-500 flex items-center">
                  <Heart className="w-3 h-3 mr-1 text-red-500" />
                  <span className="hidden sm:inline">Fleurs de la Sainte Famille</span>
                  <span className="sm:hidden">FSF</span>
                </p>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4 order-1 sm:order-2">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center px-3 sm:px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 text-sm"
                >
                  <Home className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Retour au Dashboard</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center px-3 sm:px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 text-sm"
                >
                  <LogOut className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Titre de la page */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Gestion Newsletter FSF
          </h1>
          <p className="text-gray-600 text-base sm:text-lg">
            Gérez les abonnements à la newsletter de la communauté FSF
          </p>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-700">Total</h3>
                <p className="text-2xl sm:text-3xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 sm:w-12 sm:h-12 text-blue-500" />
            </div>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-700">Actifs</h3>
                <p className="text-2xl sm:text-3xl font-bold text-green-600">{stats.active}</p>
              </div>
              <UserCheck className="w-8 h-8 sm:w-12 sm:h-12 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-700">Inactifs</h3>
                <p className="text-2xl sm:text-3xl font-bold text-red-600">{stats.inactive}</p>
              </div>
              <UserX className="w-8 h-8 sm:w-12 sm:h-12 text-red-500" />
            </div>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border-l-4 border-purple-500 col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-700">Ce mois</h3>
                <p className="text-2xl sm:text-3xl font-bold text-purple-600">{stats.thisMonth}</p>
              </div>
              <TrendingUp className="w-8 h-8 sm:w-12 sm:h-12 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Filtres, recherche et export */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-2 sm:space-y-0">
            <h3 className="text-lg font-bold text-gray-900">
              Filtres et recherche
            </h3>
            <button
              onClick={handleExport}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm sm:text-base w-full sm:w-auto justify-center sm:justify-start"
            >
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Exporter CSV</span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Recherche */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rechercher
              </label>
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Email ou nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Filtre par statut */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-sm sm:text-base"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actifs</option>
                <option value="inactive">Inactifs</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste des abonnements */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-bold text-gray-900">
              Abonnements Newsletter ({subscriptions.length})
            </h3>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des abonnements...</p>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun abonnement trouvé</p>
            </div>
          ) : (
            <>
              {/* Version mobile : cartes */}
              <div className="block lg:hidden">
                <div className="divide-y divide-gray-200">
                  {subscriptions.map((subscription) => (
                    <div key={subscription.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Mail className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {subscription.email}
                            </p>
                            {subscription.name && (
                              <p className="text-sm text-gray-500 truncate">
                                {subscription.name}
                              </p>
                            )}
                            <div className="mt-1">
                              {getStatusBadge(subscription.is_active)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          <button
                            onClick={() => openSubscriptionModal(subscription)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Voir les détails"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!isReadOnly && (
                          <button
                            onClick={() => toggleSubscriptionStatus(subscription.id, subscription.is_active)}
                            disabled={loadingActions[subscription.id]}
                            className={`p-2 rounded-lg transition-colors ${
                              loadingActions[subscription.id]
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : subscription.is_active
                                  ? 'text-red-600 hover:bg-red-50'
                                  : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={subscription.is_active ? 'Désactiver' : 'Activer'}
                          >
                            {loadingActions[subscription.id] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                            ) : subscription.is_active ? (
                              <UserX className="w-4 h-4" />
                            ) : (
                              <UserCheck className="w-4 h-4" />
                            )}
                          </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Préférences:</span>
                          <p className="text-gray-900 text-xs mt-1 line-clamp-2">
                            {formatPreferences(subscription.email_preferences)}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">Fréquence:</span>
                          <p className="text-gray-900 text-xs mt-1">
                            {getFrequencyLabel(subscription.email_preferences)}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">Inscription:</span>
                          <p className="text-gray-900 text-xs mt-1">
                            {formatDate(subscription.subscribed_at || subscription.created_at)}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">Dernier email:</span>
                          <p className="text-gray-900 text-xs mt-1">
                            {subscription.last_email_sent_at ? formatDate(subscription.last_email_sent_at) : 'Jamais'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Version desktop : tableau */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Abonné</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Statut</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Préférences</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Fréquence</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date d'inscription</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Dernier email</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {subscriptions.map((subscription) => (
                      <tr key={subscription.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Mail className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {subscription.email}
                              </p>
                              {subscription.name && (
                                <p className="text-sm text-gray-500">
                                  {subscription.name}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(subscription.is_active)}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">
                            {formatPreferences(subscription.email_preferences)}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">
                            {getFrequencyLabel(subscription.email_preferences)}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="w-4 h-4 mr-2" />
                            {formatDate(subscription.subscribed_at || subscription.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600">
                            {subscription.last_email_sent_at ? (
                              <div className="flex items-center">
                                <Activity className="w-4 h-4 mr-2" />
                                {formatDate(subscription.last_email_sent_at)}
                              </div>
                            ) : (
                              <span className="text-gray-400">Jamais</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openSubscriptionModal(subscription)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Voir les détails"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            {!isReadOnly && (
                            <button
                              onClick={() => toggleSubscriptionStatus(subscription.id, subscription.is_active)}
                              disabled={loadingActions[subscription.id]}
                              className={`p-2 rounded-lg transition-colors ${
                                loadingActions[subscription.id]
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : subscription.is_active
                                    ? 'text-red-600 hover:bg-red-50'
                                    : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={subscription.is_active ? 'Désactiver' : 'Activer'}
                            >
                              {loadingActions[subscription.id] ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                              ) : subscription.is_active ? (
                                <UserX className="w-4 h-4" />
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                            </button>
                            )}
                            {!isReadOnly && (
                            <button
                              onClick={() => deleteSubscription(subscription.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Modal de détails de l'abonnement */}
        {showSubscriptionModal && selectedSubscription && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-0">
              {/* Header du modal */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-t-2xl">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0 pr-4">
                    <h2 className="text-xl sm:text-2xl font-bold flex items-center">
                      <Mail className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 flex-shrink-0" />
                      <span className="truncate">Détails de l'abonnement</span>
                    </h2>
                    <p className="text-blue-100 mt-1 text-sm sm:text-base">
                      Informations complètes de l'abonné
                    </p>
                  </div>
                  <button
                    onClick={closeSubscriptionModal}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors flex-shrink-0"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>
              </div>

              {/* Contenu du modal */}
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Informations principales */}
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    Informations de l'abonné
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Email</label>
                      <p className="text-gray-900 font-medium flex items-center">
                        <Mail className="w-4 h-4 mr-2 text-gray-500" />
                        {selectedSubscription.email}
                      </p>
                    </div>
                    {selectedSubscription.name && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Nom</label>
                        <p className="text-gray-900 font-medium">{selectedSubscription.name}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Statut</label>
                      <div>{getStatusBadge(selectedSubscription.is_active)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Date d'inscription</label>
                      <p className="text-gray-900 font-medium flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                        {formatDate(selectedSubscription.subscribed_at || selectedSubscription.created_at)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Préférences d'email */}
                <div className="bg-blue-50 rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Settings className="w-5 h-5 mr-2 text-blue-600" />
                    Préférences de notification
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Types de contenu</label>
                      <p className="text-gray-900 font-medium text-sm sm:text-base">
                        {formatPreferences(selectedSubscription.email_preferences)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Fréquence</label>
                      <p className="text-gray-900 font-medium text-sm sm:text-base">
                        {getFrequencyLabel(selectedSubscription.email_preferences)}
                      </p>
                    </div>
                    {selectedSubscription.email_preferences?.categories && (
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Catégories d'intérêt</label>
                        <p className="text-gray-900 font-medium text-sm sm:text-base">
                          {selectedSubscription.email_preferences.categories.join(', ') || 'Toutes'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Statistiques d'activité */}
                <div className="bg-green-50 rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Activity className="w-5 h-5 mr-2 text-green-600" />
                    Activité
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Dernier email envoyé</label>
                      <p className="text-gray-900 font-medium flex items-center">
                        {selectedSubscription.last_email_sent_at ? (
                          <>
                            <Clock className="w-4 h-4 mr-2 text-gray-500" />
                            {formatDate(selectedSubscription.last_email_sent_at)}
                          </>
                        ) : (
                          <span className="text-gray-400">Aucun email envoyé</span>
                        )}
                      </p>
                    </div>
                    {selectedSubscription.unsubscribed_at && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Date de désabonnement</label>
                        <p className="text-gray-900 font-medium flex items-center">
                          <Clock className="w-4 h-4 mr-2 text-gray-500" />
                          {formatDate(selectedSubscription.unsubscribed_at)}
                        </p>
                      </div>
                    )}
                    {selectedSubscription.ip_address && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Adresse IP d'inscription</label>
                        <p className="text-gray-900 font-medium flex items-center">
                          <Globe className="w-4 h-4 mr-2 text-gray-500" />
                          {selectedSubscription.ip_address}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions rapides */}
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Actions rapides</h3>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {!isReadOnly && (
                    <button
                      onClick={() => {
                        toggleSubscriptionStatus(selectedSubscription.id, selectedSubscription.is_active);
                        closeSubscriptionModal();
                      }}
                      className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${
                        selectedSubscription.is_active
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {selectedSubscription.is_active ? (
                        <>
                          <UserX className="w-4 h-4 mr-2" />
                          <span className="hidden sm:inline">Désactiver l'abonnement</span>
                          <span className="sm:hidden">Désactiver</span>
                        </>
                      ) : (
                        <>
                          <UserCheck className="w-4 h-4 mr-2" />
                          <span className="hidden sm:inline">Activer l'abonnement</span>
                          <span className="sm:hidden">Activer</span>
                        </>
                      )}
                    </button>
                    )}
                    <a
                      href={`mailto:${selectedSubscription.email}?subject=Newsletter FSF`}
                      className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Envoyer un email</span>
                      <span className="sm:hidden">Email</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-4 sm:px-6 py-4 rounded-b-2xl">
                <div className="flex justify-end">
                  <button
                    onClick={closeSubscriptionModal}
                    className="px-4 sm:px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base w-full sm:w-auto"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-auto p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {notification.type === 'success' ? <UserCheck className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
            <span className="text-sm sm:text-base">{notification.message}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNewsletter;